"use server";

import { createClient } from "@/lib/supabase/server";
import { z } from "zod";
import { redirect } from "next/navigation";
import { throwAuthError } from "@/lib/auth/error";
import { HOME_URL } from "@/app.config";

interface ActionResult {
  error: string | null;
}

const signInSchema = z.object({
  email: z.string().email({ message: "INVALID_EMAIL" }),
  password: z.string().min(6, { message: "WEAK_PASSWORD" }),
});

export async function signIn(
  _prevState: ActionResult,
  formData: FormData,
  redirectURL: string
): Promise<ActionResult> {
  const raw = {
    email: formData.get("email"),
    password: formData.get("password"),
  };

  const parsed = signInSchema.safeParse(raw);

  if (!parsed.success) {
    const firstError = parsed.error.issues[0];
    const errorCode = firstError.message;

    if (errorCode === "INVALID_EMAIL") {
      return { error: throwAuthError("invalidEmail").message };
    }

    if (errorCode === "WEAK_PASSWORD") {
      return { error: throwAuthError("weakPassword").message };
    }

    return { error: "Validation error" };
  }

  const { email, password } = parsed.data;

  const supabase = await createClient();
  const { error } = await supabase.auth.signInWithPassword({ email, password });

  if (error) {
    if (
      error.message.includes("Invalid login credentials") ||
      error.message.includes("invalid_credentials")
    ) {
      return { error: throwAuthError("invalidCredentials").message };
    }
    return { error: error.message };
  }

  redirect(redirectURL || HOME_URL);
}
